import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  academyAdminService,
  type AcademyConceptFilters,
  type ContentStatus,
  type UpdateConceptRequest,
  type CreateConceptRequest,
  type CreateAssetRequest,
  type AcademyAdminConcept,
  type AreaInput,
  type StageInput,
} from "@/api/services/academy.service";

export const academyKeys = {
  all: ["academy-admin"] as const,
  areas: () => [...academyKeys.all, "areas"] as const,
  concepts: (filters?: AcademyConceptFilters) =>
    [...academyKeys.all, "concepts", filters] as const,
  concept: (slug: string) => [...academyKeys.all, "concept", slug] as const,
  assetKinds: () => [...academyKeys.all, "asset-kinds"] as const,
  vocabulary: () => [...academyKeys.all, "vocabulary"] as const,
};

export function useAcademyAreas() {
  return useQuery({
    queryKey: academyKeys.areas(),
    queryFn: () => academyAdminService.listAreas(),
    staleTime: 60_000,
  });
}

export function useAcademyConcepts(filters?: AcademyConceptFilters) {
  return useQuery({
    queryKey: academyKeys.concepts(filters),
    queryFn: () => academyAdminService.listConcepts(filters),
    staleTime: 30_000,
  });
}

export function useAcademyConcept(slug?: string) {
  return useQuery({
    queryKey: academyKeys.concept(slug ?? ""),
    queryFn: () => academyAdminService.getConcept(slug as string),
    enabled: !!slug,
  });
}

// The kind registry changes only when a new question type ships, so it is
// worth caching for the length of a session rather than per navigation.
export function useAcademyAssetKinds() {
  return useQuery({
    queryKey: academyKeys.assetKinds(),
    queryFn: () => academyAdminService.listAssetKinds(),
    staleTime: 30 * 60_000,
  });
}

// Zone ids, marking ids and formation roles change only when the app
// ships new ones, so this is cached for the session like the kinds.
export function useAcademyVocabulary() {
  return useQuery({
    queryKey: academyKeys.vocabulary(),
    queryFn: () => academyAdminService.vocabulary(),
    staleTime: 30 * 60_000,
  });
}

// Every mutation invalidates the whole academy key space rather than
// patching caches by hand. An edit can change a lesson's question count,
// its status, and the live/total figures on its area card all at once —
// three caches that must not be allowed to disagree.
function useAcademyMutation<TArgs, TResult = unknown>(
  fn: (args: TArgs) => Promise<TResult>
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: academyKeys.all }),
  });
}

export function useUpdateConcept() {
  return useAcademyMutation<{ slug: string; data: UpdateConceptRequest }>(
    ({ slug, data }) => academyAdminService.updateConcept(slug, data)
  );
}

export function useSetConceptStatus() {
  return useAcademyMutation<{ slug: string; status: ContentStatus }>(
    ({ slug, status }) => academyAdminService.setConceptStatus(slug, status)
  );
}

export function useUpdateAssetPayload() {
  return useAcademyMutation<{ assetId: number; payload: unknown }>(
    ({ assetId, payload }) =>
      academyAdminService.updateAssetPayload(assetId, payload)
  );
}

export function useCreateConcept() {
  return useAcademyMutation<CreateConceptRequest, AcademyAdminConcept>((data) =>
    academyAdminService.createConcept(data)
  );
}

export function useCreateAsset() {
  return useAcademyMutation<{ slug: string; data: CreateAssetRequest }>(
    ({ slug, data }) => academyAdminService.createAsset(slug, data)
  );
}

export function useDeleteAsset() {
  return useAcademyMutation<number>((assetId) =>
    academyAdminService.deleteAsset(assetId)
  );
}

export function useSetPrerequisites() {
  return useAcademyMutation<{ slug: string; prerequisiteSlugs: string[] }>(
    ({ slug, prerequisiteSlugs }) =>
      academyAdminService.setPrerequisites(slug, prerequisiteSlugs)
  );
}

export function useCreateArea() {
  return useAcademyMutation<AreaInput>((d) => academyAdminService.createArea(d));
}

export function useUpdateArea() {
  return useAcademyMutation<{
    key: string;
    data: Partial<Omit<AreaInput, "key">>;
  }>(({ key, data }) => academyAdminService.updateArea(key, data));
}

export function useSetAreaStatus() {
  return useAcademyMutation<{ key: string; status: ContentStatus }>(
    ({ key, status }) => academyAdminService.setAreaStatus(key, status)
  );
}

export function useDeleteArea() {
  return useAcademyMutation<string>((key) =>
    academyAdminService.deleteArea(key)
  );
}

export function useCreateStage() {
  return useAcademyMutation<{ areaKey: string; data: StageInput }>(
    ({ areaKey, data }) => academyAdminService.createStage(areaKey, data)
  );
}

export function useUpdateStage() {
  return useAcademyMutation<{
    areaKey: string;
    stageKey: string;
    data: Partial<Omit<StageInput, "key">>;
  }>(({ areaKey, stageKey, data }) =>
    academyAdminService.updateStage(areaKey, stageKey, data)
  );
}

export function useDeleteStage() {
  return useAcademyMutation<{ areaKey: string; stageKey: string }>(
    ({ areaKey, stageKey }) =>
      academyAdminService.deleteStage(areaKey, stageKey)
  );
}

export function useMoveConcept() {
  return useAcademyMutation<{
    slug: string;
    areaKey: string;
    stageKey: string;
  }>(({ slug, areaKey, stageKey }) =>
    academyAdminService.moveConcept(slug, areaKey, stageKey)
  );
}

export function useReorderAssets() {
  return useAcademyMutation<{ slug: string; assetIds: number[] }>(
    ({ slug, assetIds }) => academyAdminService.reorderAssets(slug, assetIds)
  );
}
